<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link to="/app/users/list" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Add User</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Full Name <span class="text-danger">*</span></label>
                  <input v-model="form.fullName" type="text" class="form-control" required />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Email <span class="text-danger">*</span></label>
                  <input v-model="form.email" type="email" class="form-control" required />
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Password <span class="text-danger">*</span></label>
                  <input v-model="form.password" type="password" class="form-control" required minlength="8" />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Confirm Password <span class="text-danger">*</span></label>
                  <input v-model="form.passwordConfirmation" type="password" class="form-control" required />
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Role <span class="text-danger">*</span></label>
                  <select v-model="form.role" class="form-select" required>
                    <option value="admin">Admin</option>
                    <option value="content_manager">Content Manager</option>
                    <option value="reviewer">Reviewer</option>
                  </select>
                  <small class="text-muted">Note: Super Admin can only be created via CLI</small>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Create User
                </button>
                <router-link to="/app/users/list" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

// API Base Path
const API_BASE = '/v1/admin'

export default {
  name: 'AddUser',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    const alert = useAlert()
    const isSuperAdmin = computed(() => authStore.isSuperAdmin)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Users', url: '/app/users/list' },
      { title: 'Add User', active: true }
    ]

    const form = ref({
      fullName: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      role: 'content_manager'
    })
    const loading = ref(false)
    const error = ref('')

    /**
     * Create admin user
     * POST /api/v1/admin/admin-users
     * Note: Super Admin cannot be created via API
     */
    const handleSubmit = async () => {
      if (form.value.password !== form.value.passwordConfirmation) {
        error.value = 'Passwords do not match'
        return
      }

      loading.value = true
      error.value = ''

      try {
        await api.post(`${API_BASE}/admin-users`, {
          email: form.value.email,
          password: form.value.password,
          fullName: form.value.fullName,
          role: form.value.role,
          createdBy: authStore.user?.id
        })
        alert.success('Success', 'User created successfully')
        router.push('/app/users/list')
      } catch (err) {
        error.value = err.response?.data?.error || 'Failed to create user'
      } finally {
        loading.value = false
      }
    }

    return { breadcrumbs, form, loading, error, isSuperAdmin, handleSubmit }
  }
}
</script>
